import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import BrandSelect from '../ui/BrandSelect';
import { modalSelect } from '../utils/ReactSelectStyles';
import { Alert, AlertDescription } from '../ui/alert';
import { Building2, Info } from 'lucide-react';
import { Actions as CONSULTANT_ACTIONS } from '../../store/actions/consultantClientsActions';
import { Actions as AGREEMENTS } from '../../store/actions/agreementsActions';
import { toggleAlertMessage } from '../../store/action';
import {
  clientsMissingSchedule,
  friendlyConsultantClientError,
} from '../utils/consultantClientUtils';

const OnboardExistingClientWizard = ({
  open,
  onClose,
  onSuccess,
  clients,
  consultantParentId,
  onboardExistingConsultantClient,
  getMyAgreements,
  showAlert,
}) => {
  const [organisationId, setOrganisationId] = useState('');
  const [perReportFeeVal, setPerReportFeeVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [masterPerReportFeeVal, setMasterPerReportFeeVal] = useState(null);
  const [currencySymbol, setCurrencySymbol] = useState('£');

  const missingScheduleClients = useMemo(
    () => clientsMissingSchedule(clients, consultantParentId),
    [clients, consultantParentId],
  );

  const clientOptions = useMemo(
    () =>
      missingScheduleClients.map((c) => ({
        value: c.id,
        label: c.name || c.accountName || c.id,
      })),
    [missingScheduleClients],
  );

  useEffect(() => {
    if (!open) {
      setOrganisationId('');
      setPerReportFeeVal('');
      setMasterPerReportFeeVal(null);
      setCurrencySymbol('£');
      return;
    }
    if (consultantParentId) {
      getMyAgreements(consultantParentId).then((data) => {
        const agreements = data?.agreements || [];
        const master = agreements.find(
          (a) => a.type === 'Consultant' && (a.status === 'complete' || a.active === true),
        );
        if (master?.billingCurrency) setCurrencySymbol(master.billingCurrency);
        const fee = master?.perReportFeeVal;
        if (fee != null && fee !== '' && !Number.isNaN(Number(fee))) {
          setMasterPerReportFeeVal(Number(fee));
          setPerReportFeeVal(String(Number(fee)));
        }
      });
    }
  }, [open, consultantParentId, getMyAgreements]);

  const onSubmit = async () => {
    if (!organisationId) {
      showAlert(true, 'Select a client to onboard.', 'error', 5000);
      return;
    }
    if (perReportFeeVal === '' || Number(perReportFeeVal) <= 0) {
      showAlert(true, 'Enter the per-report fee.', 'error', 5000);
      return;
    }

    setSaving(true);
    const result = await onboardExistingConsultantClient({
      organisationId,
      planType: 'per_report',
      perReportFeeVal: Number(perReportFeeVal),
    });
    setSaving(false);

    if (result.ok) {
      showAlert(true, 'Existing client onboarded to billing schedule.', 'success', 5000);
      onSuccess?.(result.data);
    } else {
      showAlert(true, friendlyConsultantClientError(result.error || result.message), 'error', 6000);
    }
  };

  const noClientsToOnboard = clientOptions.length === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent onClose={onClose} className='bg-card text-card-foreground border-border max-w-lg font-body'>
        <DialogHeader className='pr-14'>
          <DialogTitle>Onboard existing client</DialogTitle>
          <DialogDescription className='mt-1.5'>
            Link a client organisation you already manage to your billing schedule.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {noClientsToOnboard ? (
            <div className='flex flex-col items-center gap-2 py-6 text-center'>
              <span className='flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground'>
                <Building2 size={20} strokeWidth={1.5} />
              </span>
              <p className='text-sm font-semibold text-foreground'>Every client already has a billing schedule</p>
              <p className='text-xs text-muted-foreground'>
                Use <strong>Add client</strong> on the portfolio to create a new one.
              </p>
            </div>
          ) : (
            <div className='space-y-5'>
              <div>
                <Label htmlFor='onboard-client-organisation' className='mb-1.5 block'>
                  Client organisation <span className='text-destructive'>*</span>
                </Label>
                <BrandSelect
                  inputId='onboard-client-organisation'
                  options={clientOptions}
                  value={clientOptions.find((o) => o.value === organisationId) || null}
                  onChange={(opt) => setOrganisationId(opt ? opt.value : '')}
                  styles={modalSelect}
                  isSearchable
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition='fixed'
                  maxMenuHeight={240}
                  placeholder='Search clients without a schedule…'
                />
                <p className='text-xs text-muted-foreground mt-1.5'>
                  {clientOptions.length} {clientOptions.length === 1 ? 'client is' : 'clients are'} waiting for a
                  schedule.
                </p>
              </div>

              <div>
                <Label htmlFor='onboard-per-report-fee' className='mb-1.5 block'>
                  Per-report fee <span className='text-destructive'>*</span>
                </Label>
                <div className='relative'>
                  <span className='pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground'>
                    {currencySymbol}
                  </span>
                  <Input
                    id='onboard-per-report-fee'
                    type='number'
                    min='0'
                    step='0.01'
                    className='pl-8'
                    value={perReportFeeVal}
                    onChange={(e) => setPerReportFeeVal(e.target.value)}
                  />
                </div>
                {masterPerReportFeeVal != null && (
                  <p className='text-xs text-muted-foreground mt-1.5'>
                    Pre-filled from your consultant master agreement ({currencySymbol}
                    {masterPerReportFeeVal} per report).
                  </p>
                )}
              </div>

              <Alert variant='info'>
                <Info size={16} strokeWidth={1.5} aria-hidden />
                <AlertDescription className='text-xs text-foreground'>
                  Per-report is the default plan. Commitment end dates are set by CYF admin, using the organisation
                  subscription end date when there is one.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </DialogBody>

        <DialogFooter className='border-t border-border'>
          <Button variant='outline' onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button loading={saving} onClick={onSubmit} disabled={noClientsToOnboard}>
            Onboard client
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  clients: state.consultantClients?.clients || [],
  consultantParentId: state.profile?.details?.consultantAdmin,
});

const mapDispatchToProps = (dispatch) => ({
  onboardExistingConsultantClient: (body) => dispatch(CONSULTANT_ACTIONS.onboardExistingConsultantClient(body)),
  getMyAgreements: (organisationId) => dispatch(AGREEMENTS.getMyAgreements(organisationId)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(toggleAlertMessage(status, message, severity, hideDuration)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OnboardExistingClientWizard);
